import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
  alpha,
  useTheme,
} from "@mui/material";

const TextFieldNew = ({
  label,
  value,
  onChange,
  type = "text",
  multiline = false,
  rows = 4,
  fullWidth = false,
  startIcon = null,
  endIcon = null,
  helperText = "",
  error = false,
  disabled = false,
  required = false,
  ...props
}) => {
  const theme = useTheme();

  if (multiline) {
    return (
      <TextField
        label={label}
        value={value}
        onChange={onChange}
        multiline
        rows={rows}
        fullWidth={fullWidth}
        error={error}
        helperText={helperText}
        disabled={disabled}
        required={required}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
            transition: "all 0.2s",
            backgroundColor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.background.paper, 0.6)
                : alpha(theme.palette.background.paper, 0.8),
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.primary.main,
              borderWidth: "1px",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.primary.main,
              borderWidth: "2px",
            },
          },
          "& .MuiInputLabel-root": {
            "&.Mui-focused": {
              color: theme.palette.primary.main,
            },
          },
        }}
        {...props}
      />
    );
  }

  return (
    <FormControl
      fullWidth={fullWidth}
      variant="outlined"
      error={error}
      disabled={disabled}
      required={required}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
          transition: "all 0.2s",
          backgroundColor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.background.paper, 0.6)
              : alpha(theme.palette.background.paper, 0.8),
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.main,
            borderWidth: "1px",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.main,
            borderWidth: "2px",
          },
        },
        "& .MuiInputLabel-root": {
          "&.Mui-focused": {
            color: theme.palette.primary.main,
          },
        },
      }}
    >
      <InputLabel>{label}</InputLabel>
      <OutlinedInput
        type={type}
        value={value}
        onChange={onChange}
        label={label}
        startAdornment={
          startIcon ? (
            <InputAdornment position="start">
              <Box sx={{ color: theme.palette.primary.main, mr: 0.5 }}>
                {startIcon}
              </Box>
            </InputAdornment>
          ) : null
        }
        endAdornment={
          endIcon ? (
            <InputAdornment position="end">
              <Box sx={{ color: theme.palette.primary.main, ml: 0.5 }}>
                {endIcon}
              </Box>
            </InputAdornment>
          ) : null
        }
        {...props}
      />
      {helperText && (
        <Box
          component="p"
          sx={{
            m: 0,
            mt: 0.5,
            ml: 1.5,
            fontSize: "0.75rem",
            color: error
              ? theme.palette.error.main
              : theme.palette.text.secondary,
          }}
        >
          {helperText}
        </Box>
      )}
    </FormControl>
  );
};

TextFieldNew.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any.isRequired,
  onChange: PropTypes.func.isRequired,
  type: PropTypes.string,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  fullWidth: PropTypes.bool,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  helperText: PropTypes.string,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
};

export default TextFieldNew;
